import React, { useEffect, useState } from 'react';
import { IonContent, IonItem, IonList, IonPage, IonButton, IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonInput, IonLabel, IonAlert } from '@ionic/react';
import { Header } from '../../components';
import { useHistory } from 'react-router';
import http from '../../utils/http-common';

import { get } from '../../utils/storage';
import './ExcellentCompanyPage.css';

interface Certificate {
    id: number;
    interval_id: number;
    name: string;
    company_name: string;
    contact_id: number;
    valid: number;
    deletable?: boolean;
}

const ExcellentCompaniesPage: React.FC = () => {
    const history = useHistory();
    const [certificates, setCertificates] = useState<Certificate[]>([]);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [name, setName] = useState('');
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [certificateToDelete, setCertificateToDelete] = useState<Certificate | null>(null);

    useEffect(() => {
        fetchCertificates();
        const checkAdminStatus = async () => {
            const user = await get('user');
            setIsAdmin(user?.admin === true);
        };
        checkAdminStatus();
    }, []);

    const fetchCertificates = async () => {
        try {
            const user = await get('user');
            if (!user || !user.contact.contact_id) {
                throw new Error('Keine Firmen-ID gefunden. Bitte melden Sie sich erneut an.');
            }

            const token = await get('token');
            const response = await http.get(`/excellent-company-list/${user.contact.contact_id}`);

            if (response.status === 200) {
                // Ensure we're setting an array
                const certificatesData = Array.isArray(response.data) ? response.data : response.data.data ? response.data.data : [];
                setCertificates(certificatesData);
            }
        } catch (error) {
            console.error('Error fetching certificates:', error);
            setCertificates([]); // Set empty array on error
        }
    };

    const handleRegister = (certificate: Certificate) => {
        history.push(`/mitgliederbereich/excellent-company-registration/${certificate.interval_id}/${certificate.contact_id}`);
    };

    const getStatusClass = (valid: number) => {

        if (valid === 0) {
            return 'exc-reg-state-not-registered';
        }
        if (valid === 1) {
            return 'exc-reg-state-waiting';
        }
        if (valid === 2) {
            return 'exc-reg-state-success';
        }
    };

    const getStatusText = (valid: number) => {
        if (valid === 0) {
            return 'Nicht registriert';
        }
        if (valid === 1) {
            return 'Wartet auf Überprüfung';
        }
        if (valid === 2) {
            return 'Erfolgreich registriert';
        }
    };

    const handleAddName = async () => {
        try {

            const token = await get('token');

            const response = await http.post('/add-excellent-company-interval', {
                name: name
            });
            
            if (response.status === 200) {
                setShowModal(false);
                setName('');
                // Optionally refresh the certificates list
                fetchCertificates();
            }
        } catch (error) {
            console.error('Error adding training year:', error);
        }
    };

    const handleDelete = async (certificate: Certificate) => {
        setCertificateToDelete(certificate);
        setShowDeleteAlert(true);
    };

    const confirmDelete = async () => {
        if (!certificateToDelete) return;

        try {
            const token = await get('token');
            const response = await http.post('/delete-excellent-companies-interval', {
                id: certificateToDelete.interval_id
            });
            
            if (response.status === 200) {
                // Refresh the certificates list
                fetchCertificates();
            }
        } catch (error) {
            console.error('Error deleting certificate:', error);
        } finally {
            setShowDeleteAlert(false);
            setCertificateToDelete(null);
        }
    };

    return (
        <IonPage>
            <Header name="Exzellente Ausbildungsbetriebe" />
            <IonContent>
                {isAdmin && (
                    <div style={{ textAlign: 'right', padding: '10px' }}>
                        <IonButton onClick={() => setShowModal(true)}>
                            Ausbildungsjahr hinzufügen
                        </IonButton>
                    </div>
                )}
                
                <div className="content">
                    <h2 className="exc-comp-subtitle">Zertifikate</h2>
                    {certificates.map((certificate) => (
                        <IonList key={certificate.interval_id} inset={true} className="exc-comp-item">
                            <IonItem>
                                <div className="ion-padding exc-comp-item-content-wrapper" style={{ width: '100%' }}>
                                    <div style={{ 
                                        display: 'grid',
                                        gridTemplateColumns: '1fr',
                                        gap: '20px',
                                        width: '100%',
                                        alignItems: 'center',
                                    }}>
                                        <div>
                                            <h3 className="exc-comp-title" style={{ margin: 0 }}>
                                                Ausbildungsjahr {certificate.name} {certificate.company_name}
                                            </h3>
                                        </div>

                                        <div style={{ textAlign: 'left' }}>
                                            <p className={`status-text ${getStatusClass(certificate.valid)}`} style={{ margin: '0' }}>
                                                {getStatusText(certificate.valid)}
                                            </p>
                                        </div>
                                        
                                        <div style={{ 
                                            display: 'flex',
                                            width: '100%',
                                            flexDirection: 'row',
                                            gap: '10px'
                                        }}>
                                            {certificate.deletable && isAdmin && (
                                                <IonButton 
                                                    color="danger"
                                                    onClick={() => handleDelete(certificate)}
                                                >
                                                    Löschen
                                                </IonButton>
                                            )}
                                            
                                            {certificate.valid === 0 && !isAdmin && (
                                                <IonButton 
                                                    onClick={() => handleRegister(certificate)}
                                                >
                                                    Jetzt registrieren
                                                </IonButton>
                                            )}
                                            
                                            {certificate.valid === 1 && isAdmin && (
                                                <IonButton 
                                                    onClick={() => handleRegister(certificate)}
                                                >
                                                    prüfen
                                                </IonButton>
                                            )}
                                            
                                            {certificate.valid === 2 && (
                                                <>
                                                    <IonButton 
                                                        color="primary"
                                                        onClick={async () => {
                                                            try {
                                                                const token = await get('token');



                                                                const response = await http.get(`/excellent-company/certificate-pdf/${certificate.id}`, {
                                                                    responseType: 'blob'
                                                                });
                                                                
                                                                const blob = new Blob([response.data], { type: 'application/pdf' });
                                                                const url = window.URL.createObjectURL(blob);
                                                                const link = document.createElement('a');
                                                                link.href = url;
                                                                link.download = `Zertifikat_${certificate.name}.pdf`;
                                                                link.click();
                                                                window.URL.revokeObjectURL(url);
                                                            } catch (error) {
                                                                console.error('Fehler beim Herunterladen des Zertifikats:', error);
                                                            }
                                                        }}
                                                    >
                                                        Zertifikat herunterladen
                                                    </IonButton>
                                                    {/* <IonButton 
                                                        color="primary"
                                                        onClick={async () => {
                                                            try {
                                                                const token = await get('token');

                                                                const response = await http.get(`/excellent-company/certificate-qr/${certificate.interval_id}`, {
                                                                    responseType: 'blob'
                                                                });
                                                                
                                                                const blob = new Blob([response.data], { type: 'image/png' });
                                                                const url = window.URL.createObjectURL(blob);
                                                                const link = document.createElement('a');
                                                                link.href = url;
                                                                link.download = `Zertifikat_QR_${certificate.name}.png`;
                                                                link.click();
                                                                window.URL.revokeObjectURL(url);
                                                            } catch (error) {
                                                                console.error('Fehler beim Herunterladen des Zertifikats:', error);
                                                            }
                                                        }}
                                                    >
                                                        QR Code anzeigen
                                                    </IonButton> */}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </IonItem>
                        </IonList>
                    ))}
                </div>

                <IonModal isOpen={showModal} onDidDismiss={() => setShowModal(false)}>
                    <IonHeader>
                        <IonToolbar>
                            <IonTitle>Ausbildungsjahr hinzufügen</IonTitle>
                            <IonButtons slot="end">
                                <IonButton onClick={() => setShowModal(false)}>Schließen</IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonHeader>
                    <IonContent className="ion-padding">
                        <IonItem>
                            <IonLabel position="stacked">Ausbildungsjahr:</IonLabel>
                            <IonInput
                                value={name}
                                onIonChange={e => setName(e.detail.value || '')}
                                placeholder="z.B. 2024/2025"
                            />
                        </IonItem>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                            <IonButton onClick={() => setShowModal(false)} color="medium">
                                Abbrechen
                            </IonButton>
                            <IonButton onClick={handleAddName}>
                                Speichern
                            </IonButton>
                        </div>
                    </IonContent>
                </IonModal>

                <IonAlert
                    isOpen={showDeleteAlert}
                    onDidDismiss={() => setShowDeleteAlert(false)}
                    header="Zertifikat löschen"
                    message="Möchten Sie dieses Zertifikat wirklich löschen?"
                    buttons={[
                        {
                            text: 'Abbrechen',
                            role: 'cancel',
                            cssClass: 'secondary'
                        },
                        {
                            text: 'Löschen',
                            cssClass: 'danger',
                            handler: confirmDelete
                        }
                    ]}
                />
            </IonContent>
        </IonPage>
    );
};

export default ExcellentCompaniesPage; 